import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// Mobile-first business home — LIGHT MODE, calendar-first.
// The user's live schedule (their built-in calendar = jobs in our DB, plus any
// connected Google Calendar events), with:
//   [ + ]  top-left  -> add a job
//   [ 📷 ] top-right -> snap a photo -> instant estimate
// PRIVACY: address shows to the OWNER (their own file) but is never sent to the
// customer / Stripe — that gate lives in the payment flow, not here.

function resolveOwnerId() {
  // Single-owner for now (no real auth yet). Resolve to ONE stable id so every
  // surface — phone, web, desktop — shows the SAME provisioned calendar, instead
  // of picking up a stale conversation/user id and splitting into empty calendars.
  // TODO: replace with the signed-in session id when auth lands.
  try { return localStorage.getItem('howto_owner_id') || 'owner-default'; }
  catch (_) { return 'owner-default'; }
}
const fileToDataUrl = (f) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f);
});
const fmtMoney = (n) => '$' + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const timeStr = (d) => d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const ymd = (d) => d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WD = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export default function HomeScreen() {
  const ownerId = resolveOwnerId();
  // The Google Calendar belongs ONLY on the phone app — not the desktop app or
  // wide web. Phone = small viewport AND not the desktop Electron shell. On
  // desktop we fall back to the built-in job grid (no Google embed / connect).
  const isPhone = typeof window !== 'undefined'
    && window.innerWidth < 768
    && !(window.electronAPI && window.electronAPI.onFullScreenChange);
  const [jobs, setJobs] = useState([]);
  const [events, setEvents] = useState([]);
  const [calConnected, setCalConnected] = useState(null);
  const [gcalSync, setGcalSync] = useState({ configured: false, embedUrl: null });
  const [embedUrl, setEmbedUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthCursor, setMonthCursor] = useState(() => new Date());
  const [selected, setSelected] = useState(() => new Date());
  const [showAdd, setShowAdd] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [toast, setToast] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [jr, sch, conn, up] = await Promise.all([
        fetch(`/api/jobs?ownerId=${encodeURIComponent(ownerId)}`).then((r) => r.json()).catch(() => ({})),
        fetch(`/api/schedule?ownerId=${encodeURIComponent(ownerId)}`).then((r) => r.json()).catch(() => ({})),
        fetch(`/api/calendar/connected?userId=${encodeURIComponent(ownerId)}`).then((r) => r.json()).catch(() => ({})),
        fetch(`/api/calendar/upcoming?userId=${encodeURIComponent(ownerId)}&max=50`).then((r) => r.json()).catch(() => ({})),
      ]);
      setJobs(Array.isArray(jr.jobs) ? jr.jobs : []);
      setGcalSync({ configured: !!sch.configured, embedUrl: sch.embedUrl || null });
      setCalConnected(!!conn.connected);
      // When connected, pull the REAL Google Calendar embed URL (week view) so we
      // can iframe the genuine Google Calendar UI. Owner-only; never made public.
      if (conn.connected) {
        fetch(`/api/calendar/embed?userId=${encodeURIComponent(ownerId)}&mode=WEEK`)
          .then((r) => r.json())
          .then((em) => { if (em && em.ok && em.embedUrl) setEmbedUrl(em.embedUrl); })
          .catch(() => {});
      } else { setEmbedUrl(null); }
      // The owner's own Google Calendar (OAuth) is the live source of jobs. The
      // service-account /api/schedule path is a fallback for auto-provisioned calendars.
      const calEvents = (up && up.ok && Array.isArray(up.events)) ? up.events
        : (Array.isArray(sch.events) ? sch.events : []);
      setEvents(calEvents);
    } finally { setLoading(false); }
  }, [ownerId]);
  useEffect(() => { load(); }, [load]);
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };

  // All schedule items keyed by day.
  const items = useMemo(() => ([
    ...jobs.filter((j) => j.scheduled_at).map((j) => ({
      kind: 'job', id: 'j' + j.id, when: new Date(j.scheduled_at),
      title: j.customer_name || j.title || `Job #${j.id}`,
      sub: j.address || j.description || '', status: j.status, quote: j.quote_high || j.quote_low,
    })),
    ...events.map((e) => ({
      kind: 'cal', id: 'e' + e.id, when: new Date(e.start),
      title: e.title || 'Event', sub: e.location || '', status: 'calendar',
    })),
  ]), [jobs, events]);
  const byDay = useMemo(() => {
    const m = {}; for (const it of items) { const k = ymd(it.when); (m[k] = m[k] || []).push(it); }
    for (const k in m) m[k].sort((a, b) => a.when - b.when);
    return m;
  }, [items]);

  // Build the month grid (6 rows x 7).
  const grid = useMemo(() => {
    const y = monthCursor.getFullYear(), m = monthCursor.getMonth();
    const first = new Date(y, m, 1);
    const start = new Date(first); start.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [monthCursor]);

  const today = new Date();
  const dayItems = byDay[ymd(selected)] || [];
  const selLabel = selected.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  async function createJob(form) {
    try {
      const r = await fetch('/api/jobs', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ownerId, customer: { name: form.name, phone: form.phone, email: form.email },
          address: form.address, trade: 'junk_removal', description: form.notes, scheduledAt: form.when || undefined,
        }),
      }).then((x) => x.json());
      setShowAdd(false);
      if (r.ok) { flash('Job added to your schedule'); if (form.when) setSelected(new Date(form.when)); load(); }
      else flash(r.error || 'Could not add job');
    } catch (_) { flash('Could not add job'); }
  }

  async function onPhoto(e) {
    const f = e.target.files && e.target.files[0];
    if (f) {
      setEstimate(null); setEstimating(true);
      try {
        const dataUrl = await fileToDataUrl(f);
        const jr = await fetch('/api/jobs', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ownerId, trade: 'junk_removal', description: 'Photo estimate' }),
        }).then((x) => x.json());
        if (!jr.ok) throw new Error(jr.error || 'job create failed');
        const est = await fetch(`/api/jobs/${jr.jobId}/estimate`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ownerId, imageBase64: dataUrl }),
        }).then((x) => x.json());
        if (est.ok && est.estimate) { setEstimate({ ...est.estimate, jobId: jr.jobId }); load(); }
        else flash(est.error || 'Estimate failed');
      } catch (_) { flash('Estimate failed — try again'); }
      finally { setEstimating(false); }
    }
    e.target.value = '';
  }

  const shiftMonth = (n) => { const d = new Date(monthCursor); d.setMonth(d.getMonth() + n); setMonthCursor(d); };

  return (
    <div className="hs">
      <style>{HS_CSS}</style>

      {/* Big labeled "New Job" button RIGHT ABOVE the schedule (owner spec). Opens the
          same add-job form (what/name/address/phone/email/when) that POSTs to /api/jobs
          → Aria files it + texts/emails the owner + drops it on the calendar. */}
      <button className="hs-newjob" onClick={() => setShowAdd(true)}
        style={{ display: 'block', width: 'calc(100% - 28px)', margin: '10px auto 4px', padding: '14px 16px', borderRadius: 14, border: 'none', background: '#1a73e8', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(26,115,232,.35)' }}>
        + New Job
      </button>

      <header className="hs-top">
        <button className="hs-fab" aria-label="Add job" onClick={() => setShowAdd(true)}>
          <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
        </button>
        <div className="hs-title"><small>My schedule</small><b>{MONTHS[monthCursor.getMonth()]} {monthCursor.getFullYear()}</b></div>
        <button className="hs-fab" aria-label="Photo estimate" onClick={() => fileRef.current && fileRef.current.click()}>
          <svg viewBox="0 0 24 24"><path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" /><circle cx="12" cy="13" r="3.2" /></svg>
        </button>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhoto} />
      </header>

      {isPhone && (embedUrl || gcalSync.embedUrl) ? (
        <iframe className="hs-embed" title="Your Google Calendar" src={embedUrl || gcalSync.embedUrl} frameBorder="0" />
      ) : (
      <>
      <div className="hs-cal">
        <div className="hs-cal-nav">
          <button onClick={() => shiftMonth(-1)} aria-label="Previous month">‹</button>
          <button className="hs-today-btn" onClick={() => { const t = new Date(); setMonthCursor(t); setSelected(t); }}>Today</button>
          <button onClick={() => shiftMonth(1)} aria-label="Next month">›</button>
        </div>
        <div className="hs-wd">{WD.map((d, i) => <span key={i}>{d}</span>)}</div>
        <div className="hs-grid">
          {grid.map((d, i) => {
            const out = d.getMonth() !== monthCursor.getMonth();
            const isToday = sameDay(d, today);
            const isSel = sameDay(d, selected);
            const has = !!byDay[ymd(d)];
            return (
              <button key={i} className={`hs-cell${out ? ' out' : ''}${isToday ? ' today' : ''}${isSel ? ' sel' : ''}`} onClick={() => setSelected(new Date(d))}>
                <span>{d.getDate()}</span>
                {has ? <i className="hs-dot" /> : null}
              </button>
            );
          })}
        </div>
      </div>

      {isPhone && calConnected === false && !gcalSync.configured && (
        <button className="hs-connect" onClick={() => { window.location.href = `/api/calendar/oauth/url?userId=${encodeURIComponent(ownerId)}`; }}>
          <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></svg>
          Connect Google Calendar — see your real jobs here
        </button>
      )}
      {isPhone && calConnected && (
        <div className="hs-connect synced">
          <svg viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5" /></svg>
          Google Calendar connected — your schedule is live
        </div>
      )}
      {isPhone && gcalSync.configured && gcalSync.embedUrl && (
        <button className="hs-connect" onClick={() => window.open(gcalSync.embedUrl, 'gcal', 'width=900,height=720')}>
          <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></svg>
          Open in Google Calendar — your schedule syncs here
        </button>
      )}

      <div className="hs-day-head">{selLabel}</div>
      <main className="hs-body">
        {loading ? (
          <div className="hs-sk"><div /><div /></div>
        ) : dayItems.length === 0 ? (
          <div className="hs-empty">
            <p>Nothing scheduled this day.</p>
            <button className="hs-cta" onClick={() => setShowAdd(true)}>+ Add a job</button>
          </div>
        ) : (
          dayItems.map((it) => (
            <div className={`hs-item ${it.kind}`} key={it.id}>
              <div className="hs-time">{timeStr(it.when)}</div>
              <div className="hs-info">
                <div className="hs-row1">
                  <b>{it.title}</b>
                  {it.quote ? <span className="hs-quote">{fmtMoney(it.quote)}</span>
                    : <span className={`hs-badge s-${it.status}`}>{it.status}</span>}
                </div>
                {it.sub ? <div className="hs-sub">{it.sub}</div> : null}
              </div>
            </div>
          ))
        )}
      </main>
      </>
      )}

      {showAdd && <AddJobModal onClose={() => setShowAdd(false)} onSave={createJob} />}
      {(estimating || estimate) && <EstimateModal busy={estimating} estimate={estimate} onClose={() => { setEstimate(null); setEstimating(false); }} />}
      {toast && <div className="hs-toast">{toast}</div>}
    </div>
  );
}

function AddJobModal({ onClose, onSave }) {
  const [f, setF] = useState({ name: '', phone: '', email: '', address: '', when: '', notes: '' });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div className="hs-backdrop" onClick={onClose}>
      <div className="hs-modal" onClick={(e) => e.stopPropagation()}>
        <h3>New job</h3>
        <div className="hs-field"><label>Customer name</label><input value={f.name} onChange={set('name')} placeholder="Jane Doe" /></div>
        <div className="hs-2col">
          <div className="hs-field"><label>Phone</label><input value={f.phone} onChange={set('phone')} placeholder="(555) 123-4567" /></div>
          <div className="hs-field"><label>Email</label><input value={f.email} onChange={set('email')} placeholder="jane@email.com" /></div>
        </div>
        <div className="hs-field"><label>Address <span className="hs-priv">private · your files only</span></label><input value={f.address} onChange={set('address')} placeholder="412 Maple Ave" /></div>
        <div className="hs-field"><label>When</label><input type="datetime-local" value={f.when} onChange={set('when')} /></div>
        <div className="hs-field"><label>What to haul</label><textarea rows={2} value={f.notes} onChange={set('notes')} placeholder="Old sectional + mattress, 2nd floor" /></div>
        <div className="hs-actions">
          <button className="hs-ghost" onClick={onClose}>Cancel</button>
          <button className="hs-cta" onClick={() => onSave(f)} disabled={!f.name && !f.phone}>Add job</button>
        </div>
      </div>
    </div>
  );
}

function EstimateModal({ busy, estimate, onClose }) {
  return (
    <div className="hs-backdrop" onClick={busy ? undefined : onClose}>
      <div className="hs-modal" onClick={(e) => e.stopPropagation()}>
        {busy ? (
          <div className="hs-est-busy"><span className="hs-spin" /><b>Reading the photo…</b><p>Sizing up the load and pricing it.</p></div>
        ) : estimate ? (
          <>
            <h3>Estimate</h3>
            <div className="hs-est-amt">{fmtMoney(estimate.quote && estimate.quote.low)} – {fmtMoney(estimate.quote && estimate.quote.high)}</div>
            {estimate.jobSummary ? <p className="hs-est-sum">{estimate.jobSummary}</p> : null}
            {Array.isArray(estimate.scope) && estimate.scope.length ? (
              <ul className="hs-est-scope">{estimate.scope.slice(0, 5).map((s, i) => <li key={i}>{s}</li>)}</ul>
            ) : null}
            <p className="hs-est-note">Draft — you set the final price before it goes to the customer.</p>
            <div className="hs-actions"><button className="hs-cta" onClick={onClose}>Done</button></div>
          </>
        ) : null}
      </div>
    </div>
  );
}

const HS_CSS = `
.hs{--cyan:#06b6d4;--cyan-d:#0891b2;--ink:#0f2a43;--muted:#6b8198;--bg:#f3f6f9;--card:#ffffff;--line:#e4eaf0;
  position:relative;height:100%;display:flex;flex-direction:column;color:var(--ink);background:var(--bg);
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;overflow:hidden}
.hs-top{display:flex;align-items:center;justify-content:space-between;padding:16px 16px 10px;gap:10px;background:var(--card);border-bottom:1px solid var(--line)}
.hs-title{text-align:center;line-height:1.15}
.hs-title small{display:block;color:var(--muted);font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px}
.hs-title b{font-size:17px}
.hs-fab{width:44px;height:44px;flex:0 0 44px;border:0;border-radius:14px;cursor:pointer;display:grid;place-items:center;
  color:#fff;background:linear-gradient(135deg,var(--cyan),#22d3ee);box-shadow:0 6px 16px rgba(6,182,212,.35);transition:transform .15s,box-shadow .15s}
.hs-fab:hover{transform:translateY(-2px);box-shadow:0 10px 22px rgba(6,182,212,.45)}
.hs-fab:active{transform:translateY(0)}
.hs-fab svg{width:22px;height:22px;fill:none;stroke:#fff;stroke-width:2.3;stroke-linecap:round;stroke-linejoin:round}
.hs-cal{background:var(--card);padding:6px 12px 14px;border-bottom:1px solid var(--line)}
.hs-cal-nav{display:flex;align-items:center;justify-content:center;gap:18px;margin:4px 0 10px}
.hs-cal-nav button{border:0;background:transparent;color:var(--ink);font-size:24px;line-height:1;cursor:pointer;width:34px;height:34px;border-radius:10px}
.hs-cal-nav button:hover{background:var(--bg)}
.hs-today-btn{font-size:13px!important;font-weight:700;color:var(--cyan-d)!important;width:auto!important;padding:0 12px}
.hs-wd{display:grid;grid-template-columns:repeat(7,1fr);margin-bottom:4px}
.hs-wd span{text-align:center;font-size:11px;font-weight:700;color:var(--muted)}
.hs-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:2px}
.hs-cell{position:relative;aspect-ratio:1;border:0;background:transparent;border-radius:11px;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:600;color:var(--ink);transition:background .12s}
.hs-cell:hover{background:var(--bg)}
.hs-cell.out{color:#c4d0db}
.hs-cell.today{color:var(--cyan-d);font-weight:800}
.hs-cell.today::after{content:'';position:absolute;inset:5px;border:1.5px solid var(--cyan);border-radius:10px}
.hs-cell.sel{background:var(--cyan);color:#fff!important}
.hs-cell.sel::after{display:none}
.hs-cell.sel .hs-dot{background:#fff}
.hs-dot{position:absolute;bottom:6px;width:5px;height:5px;border-radius:50%;background:var(--cyan)}
.hs-connect{margin:10px 14px 0;display:flex;align-items:center;gap:9px;width:calc(100% - 28px);background:rgba(6,182,212,.08);
  border:1px solid rgba(6,182,212,.28);color:var(--cyan-d);border-radius:12px;padding:10px 13px;font-size:12.5px;font-weight:600;cursor:pointer;text-align:left}
.hs-connect svg{width:16px;height:16px;flex:0 0 16px;fill:none;stroke:var(--cyan-d);stroke-width:1.8}
.hs-connect.synced{background:rgba(10,157,110,.09);border-color:rgba(10,157,110,.3);color:#0a9d6e;cursor:default}
.hs-connect.synced svg{stroke:#0a9d6e;stroke-width:2.4;stroke-linecap:round;stroke-linejoin:round}
.hs-embed{flex:1;min-height:0;width:100%;border:0;background:var(--card)}
.hs-day-head{padding:16px 16px 8px;font-size:13px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.4px}
.hs-body{flex:1;min-height:0;overflow-y:auto;padding:0 14px 90px}
.hs-item{display:flex;gap:12px;background:var(--card);border:1px solid var(--line);border-radius:14px;padding:13px 14px;margin-bottom:9px;box-shadow:0 1px 3px rgba(15,42,67,.04);animation:hsr .4s ease both}
@keyframes hsr{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.hs-item.job{border-left:3px solid var(--cyan)}
.hs-item.cal{border-left:3px solid #7c93ff}
.hs-time{font-size:12.5px;font-weight:700;color:var(--ink);min-width:60px;padding-top:1px}
.hs-info{flex:1;min-width:0}
.hs-row1{display:flex;align-items:center;justify-content:space-between;gap:8px}
.hs-row1 b{font-size:14.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.hs-sub{color:var(--muted);font-size:12.5px;margin-top:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.hs-quote{font-weight:800;color:#0a9d6e;font-size:13.5px;white-space:nowrap}
.hs-badge{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;padding:3px 8px;border-radius:999px;background:#eef3f7;color:var(--muted);white-space:nowrap}
.hs-badge.s-scheduled{background:rgba(6,182,212,.12);color:var(--cyan-d)}
.hs-badge.s-complete,.hs-badge.s-paid{background:rgba(10,157,110,.12);color:#0a9d6e}
.hs-badge.s-calendar{background:rgba(124,147,255,.14);color:#5a6fd6}
.hs-sk div{height:60px;border-radius:14px;margin-bottom:9px;background:linear-gradient(90deg,#eef2f6,#f6f9fb,#eef2f6);background-size:200% 100%;animation:hsk 1.2s infinite}
@keyframes hsk{to{background-position:-200% 0}}
.hs-empty{text-align:center;padding:30px 20px;color:var(--muted)}
.hs-empty p{font-size:14px;margin:0 0 14px}
.hs-cta{border:0;border-radius:12px;padding:11px 20px;font-size:14.5px;font-weight:700;cursor:pointer;color:#fff;background:linear-gradient(135deg,var(--cyan),#22d3ee);box-shadow:0 8px 18px rgba(6,182,212,.32);transition:transform .15s}
.hs-cta:hover{transform:translateY(-2px)}
.hs-cta:disabled{opacity:.5;cursor:default;transform:none}
.hs-backdrop{position:fixed;inset:0;z-index:60;background:rgba(15,42,67,.32);backdrop-filter:blur(3px);display:flex;align-items:flex-end;justify-content:center;animation:hf .2s ease}
@keyframes hf{from{opacity:0}to{opacity:1}}
.hs-modal{width:100%;max-width:460px;background:var(--card);border-radius:22px 22px 0 0;padding:22px 20px calc(22px + env(safe-area-inset-bottom));box-shadow:0 -16px 50px rgba(15,42,67,.22);animation:hu .28s cubic-bezier(.2,.8,.2,1)}
@keyframes hu{from{transform:translateY(40px)}to{transform:none}}
.hs-modal h3{margin:0 0 16px;font-size:19px}
.hs-field{margin-bottom:13px}
.hs-field label{display:block;font-size:12px;font-weight:600;color:var(--muted);margin-bottom:6px}
.hs-priv{color:#0a9d6e;font-weight:700;font-size:10.5px;margin-left:6px}
.hs-field input,.hs-field textarea{width:100%;box-sizing:border-box;background:var(--bg);border:1px solid var(--line);border-radius:12px;padding:12px;color:var(--ink);font-size:14.5px;font-family:inherit;outline:none;transition:border .15s}
.hs-field input:focus,.hs-field textarea:focus{border-color:var(--cyan)}
.hs-2col{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.hs-actions{display:flex;gap:10px;margin-top:8px}
.hs-actions .hs-cta{flex:1}
.hs-ghost{flex:1;border:1px solid var(--line);background:#fff;color:var(--ink);border-radius:12px;padding:12px;font-size:14.5px;font-weight:600;cursor:pointer}
.hs-est-busy{text-align:center;padding:18px 8px}
.hs-est-busy b{display:block;margin:14px 0 4px;font-size:16px}
.hs-est-busy p{color:var(--muted);font-size:13px;margin:0}
.hs-spin{display:inline-block;width:30px;height:30px;border:3px solid rgba(6,182,212,.25);border-top-color:var(--cyan);border-radius:50%;animation:hsp .7s linear infinite}
@keyframes hsp{to{transform:rotate(360deg)}}
.hs-est-amt{font-size:32px;font-weight:800;letter-spacing:-.5px;margin:2px 0 8px;color:var(--cyan-d)}
.hs-est-sum{font-size:14px;margin:0 0 10px}
.hs-est-scope{margin:0 0 12px;padding-left:18px;color:var(--muted);font-size:13px}
.hs-est-scope li{margin-bottom:4px}
.hs-est-note{color:var(--muted);font-size:12px;font-style:italic;margin:0 0 14px}
.hs-toast{position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:80;background:var(--ink);color:#fff;border-radius:12px;padding:11px 18px;font-size:13.5px;font-weight:600;box-shadow:0 12px 30px rgba(15,42,67,.3);animation:ht .25s ease}
@keyframes ht{from{opacity:0;transform:translate(-50%,8px)}to{opacity:1;transform:translate(-50%,0)}}
@media(min-width:560px){.hs-backdrop{align-items:center}.hs-modal{border-radius:22px}}
`;
